/**
 * router/index.tsx - Main routing configuration for the application.
 *
 * We use React Router's data-driven RouteObject API.
 * All pages are lazily loaded to minimize the initial bundle size.
 */

import type { RouteObject } from 'react-router';
import { lazy, Suspense } from 'react';
import { MainLayout } from '../layouts/main/main-layout';
import { Outlet } from 'react-router';
import { usePathname } from '../hooks/use-pathname';
import { PageLoader } from '../components/page-loader/page-loader';
import { ErrorLayout } from '../layouts/error/error-layout';

// Lazy loading components from the 'pages' folder to improve performance
const DashboardPage = lazy(() => import('../pages/dashboard-page'));
const PlanetsPage = lazy(() => import('../pages/planets-page'));
const PlanetDetailPage = lazy(() => import('../pages/planet-detail-page'));
const UsersPage = lazy(() => import('../pages/users-page'));
const UserDetailPage = lazy(() => import('../pages/user-detail-page'));
const TransactionsPage = lazy(() => import('../pages/transactions-page'));
const TransactionDetailPage = lazy(() => import('../pages/transaction-detail-page'));
const DocsPage = lazy(() => import('../pages/docs-page'));

const Page500 = lazy(() => import('../pages/500-page'));
const Page404 = lazy(() => import('../pages/404-page'));
const Page403 = lazy(() => import('../pages/403-page'));
const Offline = lazy(() => import('../pages/offline-page'));

/**
 * SuspenseOutlet wraps the router Outlet with React.Suspense.
 * The 'key={pathname}' forces a re-mount on navigation if needed,
 * showing the PageLoader skeleton while the lazy component loads.
 */
function SuspenseOutlet() {
  const pathname = usePathname();

  return (
    <Suspense key={pathname} fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  );
}

/**
 * Main application layout wrapper.
 */
const mainLayout = () => (
  <MainLayout>
    <SuspenseOutlet />
  </MainLayout>
);

/**
 * Main application layout wrapper.
 */
const errorLayout = () => (
  <ErrorLayout>
    <SuspenseOutlet />
  </ErrorLayout>
);

/**
 * Route definitions array.
 * This is exported and used by the createBrowserRouter in 'src/index.tsx'.
 */
export const routesSection: RouteObject[] = [
  {
    path: '/',
    element: mainLayout(),
    children: [
      { index: true, element: <DashboardPage /> },
      {
        path: 'planets',
        children: [
          { index: true, element: <PlanetsPage /> },
          { path: ':id', element: <PlanetDetailPage /> },
        ],
      },
      {
        path: 'users',
        children: [
          { index: true, element: <UsersPage /> },
          { path: ':id', element: <UserDetailPage /> },
        ],
      },
      {
        path: 'transactions',
        children: [
          { index: true, element: <TransactionsPage /> },
          { path: ':id', element: <TransactionDetailPage /> },
        ],
      },
      { path: 'docs', element: <DocsPage /> },
    ],
  },
  {
    path: 'error',
    element: errorLayout(),
    children: [
      { path: '500', element: <Page500 /> },
      { path: '404', element: <Page404 /> },
      { path: '403', element: <Page403 /> },
      { path: 'offline', element: <Offline /> },
    ],
  },
  // Catch-all route for 404
  { path: '*', element: <Page404 /> },
];
